import { InventoryData } from "@hooks/useMultiWarehouseInventory"

import { Category, findBestCategory } from "./findBestCategory"

/**
 * Strips the Shopify collection GID prefix to get just the numeric ID
 */
export const stripCollectionGid = (id: string): string => {
  return id?.replace("gid://shopify/Collection/", "") ?? ""
}

/**
 * Extracts all unique Shopify collections from inventory data
 */
export const extractCollectionsFromInventory = (
  inventoryData: InventoryData
): Category[] => {
  const collectionsMap = new Map<string, Category>()

  Object.values(inventoryData).forEach((skuData) => {
    // Check if this SKU data has a collections property
    // Collections are stored at the SKU level, not location level
    const collections = (skuData as any)?.collections

    if (Array.isArray(collections)) {
      collections.forEach((collection: any) => {
        const { id, title } = collection
        if (id && title && !collectionsMap.has(id)) {
          collectionsMap.set(id, { id, title })
        }
      })
    }
  })

  return Array.from(collectionsMap.values())
}

/**
 * Finds the best matching Shopify collection ID for a collection page based on:
 * - The page title/context (e.g., "iPhone 16", "Samsung Phones", "All Products")
 * - Available Shopify collections from inventory data
 *
 * Returns the numeric collection ID (with GID prefix stripped), or an empty string if no good match
 */
export const findBestCollectionForPage = (
  pageTitle: string,
  inventoryData: InventoryData
): string => {
  if (!pageTitle || !inventoryData) {
    return ""
  }

  const collections = extractCollectionsFromInventory(inventoryData)

  if (collections.length === 0) {
    return ""
  }

  const result = findBestCategory(pageTitle, collections)

  // Only return a collection ID if we have a reasonable score
  // Avoid false positives from very generic matches
  if (result.match && result.score > 0) {
    return stripCollectionGid(result.match.id)
  }

  return ""
}
